import Box from '@liquidweb-dev/undercurrent/Box';
import Card from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import Chip from '@liquidweb-dev/undercurrent/Chip';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import { formatCurrency } from '@liquidweb-dev/banana-stand/format';
import type { BillingCycleT } from '@liquidweb-dev/club-sauce/types/billingCycle';
import { createDiscountString } from 'components/AvailableAddOnCard/HookedAvailableAddOnCard';
import ProductBrandBlock from 'components/ProductBrandBlock';
import usePackageDetails from 'modules/queries/market/package/useDetails';
import cyclesToTimeUnit from 'utility/constants/cyclesToTimeUnit';

interface ProductDetailsCardProps {
	code: string;
	description?: string;
	name?: string;
	subtitle?: string;
	selectedCycle: BillingCycleT;
}

const ProductDetailsCard = ({
	code,
	name: nameProp,
	subtitle,
	description: descriptionProp,
	selectedCycle,
}: ProductDetailsCardProps) => {
	const { data: packageDetails } = usePackageDetails(
		{
			code,
			alsowith: ['defaultPriceByCycle', 'defaultProductsConfiguration'],
		},
		{ enabled: Boolean(code) },
	);

	const { title, version } = packageDetails || {};

	const name = nameProp || title || '';
	const description = descriptionProp || version?.description || '';
	const priceData = version?.defaultPriceByCycle?.find(
		({ cycle }) => cycle === selectedCycle,
	);

	const fullPrice = priceData?.cycle_price || '';
	const formattedFullPrice = formatCurrency(fullPrice);
	const discountedPrice = priceData?.cycle_discount_price || '';
	const formattedDiscountPrice = formatCurrency(discountedPrice, {
		timeUnit: cyclesToTimeUnit[selectedCycle],
	});
	const hasDiscount = Number(priceData?.discount_percent) > 0;
	const discount = createDiscountString(
		priceData?.discount_percent || '',
		priceData?.discount_cycle_repetitions || null,
	);

	return (
		<Card>
			<CardContent>
				<Stack sx={{ gap: 200 }}>
					<ProductBrandBlock code={code} />

					<Stack sx={{ gap: 150 }}>
						<Stack sx={{ gap: 50 }}>
							<Typography variant="title-xs" sx={{ fontWeight: 500 }}>
								{name}
							</Typography>
							{subtitle && (
								<Typography
									variant="body-md"
									sx={{
										fontWeight: 500,
										color: ({ vars }) => vars.palette.uc.text.medium,
									}}
								>
									{subtitle}
								</Typography>
							)}
						</Stack>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 100,
								justifyContent: 'space-between',
							}}
						>
							<Typography
								variant="body-sm"
								sx={{
									color: ({ vars }) => vars.palette.uc.text.weak,
									display: 'flex',
									alignItems: 'center',
									gap: 100,
								}}
							>
								<Typography
									sx={{
										textDecoration: hasDiscount ? 'line-through' : 'none',
									}}
								>
									{formattedFullPrice}
								</Typography>

								{hasDiscount && (
									<Typography>{formattedDiscountPrice}</Typography>
								)}
							</Typography>

							{discount && <Chip color="success" label={discount} />}
						</Box>
					</Stack>

					<Stack sx={{ gap: 50 }}>
						<Typography
							variant="label-xl"
							sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
						>
							Description
						</Typography>

						<Typography variant="body-md">{description}</Typography>
					</Stack>
				</Stack>
			</CardContent>
		</Card>
	);
};

export default ProductDetailsCard;
